import classNames from "classnames";
import { useObjectMap } from "@ascendnow/shared";
import { useEffect, useRef, useState } from "react";
import AscButton from "../AscButton/AscButton";
import AscCorrectAnswersCard from "../AscCards/AscCorrectAnswersCard/AscCorrectAnswersCard";
import { ClassicChevronLeftIcon, ClassicChevronRightIcon } from "../AscIcons";
import AscQuizQuestion from "./AscQuizQuestion/AscQuizQuestion";
import styles from "./AscQuizQuestionSetActivity.module.scss";
import { QuizQuestion, QuizQuestionAnswer } from "@ascendnow/shared";
import AscCircularProgressBar from "../AscCircularProgressBar/AscCircularProgressBar";
import AscModalClassic from "../AscModalClassic/AscModalClassic";

export interface AscQuizQuestionSetActivityProps {
  className?: string;
  questions: QuizQuestion[];
  loading?: boolean;
  attemptsCompleted?: boolean;
  onSubmitAnswers?: (
    answers: {
      questionId: string;
      answerId?: string;
    }[]
  ) => Promise<
    | {
        questionId: string;
        answerId?: string;
        correctAnswerId?: string;
        correct?: boolean;
      }[]
    | undefined
  >;
  disabled?: boolean;
  responseAnswers?: Record<string, QuizQuestionAnswer>;
  mode?: "assessment" | "quiz";
  onTouched?: () => void;
  autosave?: boolean;
  activityId?: string;
}

export default function AscQuizQuestionSetActivity({
  className,
  questions,
  loading = false,
  attemptsCompleted,
  onSubmitAnswers,
  disabled = false,
  responseAnswers,
  mode = "quiz",
  onTouched,
  autosave = false,
  activityId,
}: AscQuizQuestionSetActivityProps) {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOnLastQuestion = activeQuestionIndex === questions?.length - 1;
  const [allAnswerLoading, setAllAnswersLoading] = useState<boolean>(false);
  const [answers, answersActions] = useObjectMap<QuizQuestionAnswer>();
  const [showCorrectAnswers, setShowCorrectAnswers] = useState<boolean>(false);
  const [showRestoreDraftModal, setShowRestoreDraftModal] = useState(false);

  function handleCloseShowRestoreDraftModal() {
    setShowRestoreDraftModal(false);
    localStorage.removeItem(`ASC-ASS-${activityId}`);
  }

  function handleRestoreDraft() {
    const answersString = localStorage.getItem(`ASC-ASS-${activityId}`);
    if (!answersString) {
      return;
    }
    const answers = JSON.parse(answersString);
    answersActions.setAll(answers);
  }

  function onPrevious() {
    if (activeQuestionIndex === 0) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex - 1);
  }

  function onNext() {
    if (activeQuestionIndex >= questions?.length - 1) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex + 1);
  }

  function onNavigateWithKeyboard(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      onPrevious();
      return;
    }
    if (event.key === "ArrowRight") {
      onNext();
      return;
    }
    if (
      event.key === "Enter" &&
      (event.metaKey || event.ctrlKey || event.shiftKey)
    ) {
      handleSubmitAnswers();
      return;
    }
    if (
      answers?.[questions[activeQuestionIndex]?.id]?.showCorrectAnswer ||
      disabled ||
      allAnswerLoading
    ) {
      return;
    }
    if (event.key === "ArrowDown") {
      const question = questions[activeQuestionIndex];
      const nextAnswerIndex =
        question?.answers?.findIndex(
          (a) => a?.id === answers?.[question?.id]?.answerId
        ) + 1;
      handleAnswerChange(
        question?.id,
        question?.answers?.[
          nextAnswerIndex >= question?.answers.length ? 0 : nextAnswerIndex
        ]?.id
      );
      return;
    }
    if (event.key === "ArrowUp") {
      const question = questions[activeQuestionIndex];
      const previousAnswerIndex =
        question?.answers?.findIndex(
          (a) => a?.id === answers?.[question?.id]?.answerId
        ) - 1;
      handleAnswerChange(
        question?.id,
        question?.answers?.[
          previousAnswerIndex < 0
            ? question?.answers?.length - 1
            : previousAnswerIndex
        ]?.id
      );
    }
  }

  function handleAnswerChange(questionId: string, answerId: string) {
    onTouched?.();
    answersActions.upsert(questionId, {
      questionId,
      answerId,
    });
  }

  function handleTryAgain() {
    setShowCorrectAnswers(false);
    answersActions.setAll(
      answersActions.map(
        ([questionId, answer]) =>
          [
            questionId,
            {
              ...answer,
              correctAnswerId: undefined,
              correct: undefined,
              showCorrectAnswer: false,
            },
          ] as [string, QuizQuestionAnswer]
      )
    );
  }

  async function handleSubmitAnswers() {
    if (answersActions.length() !== questions.length || allAnswerLoading) {
      return;
    }

    setAllAnswersLoading(true);
    const correctAnswers = await onSubmitAnswers?.(
      answersActions.map(([k, a]) => a)
    );
    setShowCorrectAnswers(true);

    if (correctAnswers) {
      const _correctAnswers: [string, QuizQuestionAnswer][] =
        correctAnswers?.map((ca) => [
          ca?.questionId,
          {
            questionId: ca?.questionId,
            answerId: ca?.answerId,
            correctAnswerId: ca?.correctAnswerId,
            correct: ca?.correct,
            showCorrectAnswer: true,
          },
        ]);
      answersActions.setAll(_correctAnswers);
    }
    setAllAnswersLoading(false);
    setShowRestoreDraftModal(false);
    localStorage.removeItem(`ASC-ASS-${activityId}`);
  }

  useEffect(() => {
    if (!attemptsCompleted || !responseAnswers) return;
    answersActions.setAll(responseAnswers);
    setShowCorrectAnswers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseAnswers, attemptsCompleted]);

  useEffect(() => {
    if (!autosave || !activityId || !answersActions?.length()) {
      return;
    }
    localStorage.setItem(`ASC-ASS-${activityId}`, JSON.stringify(answers));
  }, [answers]);

  useEffect(() => {
    if (!autosave || !activityId || answersActions?.length()) {
      return;
    }
    const answersString = localStorage.getItem(`ASC-ASS-${activityId}`);
    if (!answersString) {
      return;
    }
    setShowRestoreDraftModal(true);
  }, [activityId]);

  return (
    <div
      data-testid="asc-quiz-question-set-activity"
      className={classNames(styles.ground, className)}
      onClick={() => {
        inputRef?.current?.focus();
      }}
    >
      {mode === "assessment" ? (
        <AscCircularProgressBar
          className={styles.progressBar}
          percentage={(answersActions?.length() * 100) / questions?.length}
        />
      ) : null}

      <div className={styles.questionWrapper}>
        <AscQuizQuestion
          className={styles.questionItem}
          questionNumber={
            mode === "assessment" ? activeQuestionIndex + 1 : undefined
          }
          question={questions[activeQuestionIndex]}
          onAnswerChange={handleAnswerChange}
          answer={answers?.[questions[activeQuestionIndex]?.id]?.answerId}
          attemptsCompleted={attemptsCompleted}
          disabled={
            allAnswerLoading ||
            disabled ||
            attemptsCompleted ||
            showCorrectAnswers
          }
          correctAnswerId={
            answers?.[questions[activeQuestionIndex]?.id]?.correctAnswerId
          }
        />
        <div className={styles.navigation}>
          <input
            className={styles.hiddenNavigationInput}
            ref={inputRef}
            onKeyDown={onNavigateWithKeyboard}
            inputMode="none"
          />
          <AscButton
            variant="lightGray"
            className={classNames(styles.previousButton, {
              [styles.hidden]: activeQuestionIndex === 0,
            })}
            icon={<ClassicChevronLeftIcon />}
            disabled={activeQuestionIndex === 0}
            onClick={onPrevious}
            squarishIcon
          />

          <AscButton
            variant="lightGray"
            className={classNames(styles.nextButton, {
              [styles.hidden]: isOnLastQuestion,
            })}
            icon={<ClassicChevronRightIcon />}
            loading={allAnswerLoading}
            disabled={isOnLastQuestion}
            squarishIcon
            onClick={onNext}
          />
        </div>
      </div>

      <AscCorrectAnswersCard
        type="secondary"
        shouldGoNext={!isOnLastQuestion}
        onNext={onNext}
        className={styles.answersCard}
        loading={allAnswerLoading || loading}
        disabled={
          disabled ||
          ((answersActions?.toArray()?.some(([_, a]) => !a?.answerId) ||
            answersActions?.length() !== questions?.length) &&
            isOnLastQuestion) ||
          loading
        }
        attemptsCompleted={attemptsCompleted}
        onAttempt={handleSubmitAnswers}
        onTryAgain={handleTryAgain}
        correct={
          showCorrectAnswers
            ? answersActions?.toArray()?.every(([_, a]) => a?.correct)
            : undefined
        }
        showCorrectAnswers={mode === "quiz" && showCorrectAnswers}
        checkAnswerText={isOnLastQuestion ? "Submit" : "Next"}
        tryAgainText="Try again"
        correctAnswersText="Correct answer"
        incorrectText={`Incorrect ${
          Object.values(answers).filter((el) => !el?.correct)?.length +
          (questions?.length - Object.values(answers).length) +
          "/" +
          questions?.length
        }`}
      />
      <AscModalClassic
        visible={showRestoreDraftModal}
        onClose={handleCloseShowRestoreDraftModal}
        icon="exclamation-mark"
        title="Restore answers"
        description={
          "You have answers that were saved, do you want to restore them?"
        }
        type={"cancel-and-confirm"}
        confirmButtonAction={handleRestoreDraft}
      />
    </div>
  );
}
